import { Controller } from "web-ui/controller"
import { morphdomHold } from "web-ui/morphdom/hold"
import * as Log from "web-ui/log"

// Connects to data-controller="textarea"
export default class extends Controller {
  static targets = ["textarea"]

  static values = {
    newlines: { type: Boolean, default: true }
  }

  static events = {
    "focus [data-textarea-target~='textarea']": "onFocus",
    "blur [data-textarea-target~='textarea']": "onBlur",
    "keydown [data-textarea-target~='textarea']": "preventNewline",

    // Capture so that stripNewlines cleans the value before any other input
    // listener reads it. The character counter controller reads the value on
    // input, and assigning the textarea's value fires no further input event,
    // so a counter that read first would keep a count that includes the
    // stripped newlines.
    // - Aaron, Sun Aug 23 2026
    "input [data-textarea-target~='textarea']": "onInput:capture"
  }

  connect() {
    this.debug("Connect")

    // Inside a <dialog> that opens from display: none, Chromium does not
    // always remeasure the ::after pseudo's attr(data-text) content on first
    // paint, leaving the wrapper sized for the textarea's `rows` default
    // instead of its content. Resizing here re-writes data-text, which
    // invalidates the pseudo and forces the recalculation.
    // - Aaron, Wed May 6 2026
    this.resize()
  }

  disconnect() {
    this.debug("Disconnect")

    this.releaseHold()
  }

  onInput() {
    this.stripNewlines()
    this.resize()
  }

  resize() {
    this.debug("Resizing textarea", { id: this.textareaTarget.id })

    this.element.dataset.text = this.textareaTarget.value
  }

  preventNewline(event) {
    if (this.newlinesValue) {
      return
    }

    if (event.key === "Enter") {
      event.preventDefault()
    }
  }

  stripNewlines() {
    if (this.newlinesValue) {
      return
    }

    const textarea = this.textareaTarget
    const value = textarea.value

    if (value.includes("\n") || value.includes("\r")) {
      const position = textarea.selectionStart
      const cleaned = value.replace(/[\r\n]+/g, " ")
      const positionAdjustment = value.length - cleaned.length

      textarea.value = cleaned
      textarea.selectionStart = position - positionAdjustment
      textarea.selectionEnd = position - positionAdjustment
    }
  }

  onFocus() {
    this.debug("Focus")
    this.hold ??= morphdomHold(this.element)
  }

  onBlur() {
    this.debug("Blur")
    this.releaseHold()
  }

  releaseHold() {
    if (this.hold) {
      this.hold.release()
      this.hold = null
    }
  }

  debug(message, data) {
    Log.debug("Textarea - " + message, data)
  }
}
